import React, { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import api from '../../../services/api';
import { PAUSE_PRESETS } from '../../../hooks/dashboard/useExamPrefs';

const ExamHeader = ({
    isReviewMode,
    showSettings,
    setShowSettings,
    setShowValidateModal,
    showGrid,
    setShowGrid,
    setShowRestartModal,
    stopExam,
    isFinished,
    answers,
    validateLabel,
    hasAnnexe,
    examId,
    annexeExamId, // For custom exams: the source exam ID for annexe download
    miniNav,
    title,
    pauseSec = 60,
    showAllAnswers = false,
    setPauseSec,
    setShowAllAnswers,
}) => {
    const { language, setLanguage } = useLanguage();
    const { theme, setTheme } = useTheme();
    const [loadingAnnexe, setLoadingAnnexe] = useState(false);

    // Use annexeExamId for custom exams, otherwise fall back to examId
    const effectiveAnnexeId = annexeExamId || examId;

    // Libellé compact du délai de pause auto (0 = jamais) + cycle sur les presets.
    const pauseLabel = (sec) => {
        if (sec <= 0) return language === 'fr' ? 'Jamais' : 'Never';
        if (sec < 60) return `${sec} s`;
        return `${Math.round(sec / 60)} min`;
    };
    const cyclePause = () => {
        const idx = PAUSE_PRESETS.indexOf(pauseSec);
        setPauseSec(PAUSE_PRESETS[(idx + 1) % PAUSE_PRESETS.length]);
    };

    const handleAnnexeDownload = async () => {
        if (loadingAnnexe || !effectiveAnnexeId) return;
        setLoadingAnnexe(true);
        try {
            await api.downloadAnnexeBlob(effectiveAnnexeId, `annexe_${effectiveAnnexeId}.pdf`);
        } catch (e) {
            console.error('Annexe download failed:', e);
        } finally {
            setLoadingAnnexe(false);
        }
    };

    return (
        <header data-help="session-header" className="p-4 flex items-center gap-3">
            <button
                onClick={async () => {
                    await stopExam();
                }}
                className="p-2 bg-white dark:bg-white/10 rounded-full hover:bg-gray-100 dark:hover:bg-white/20 transition border border-gray-200 dark:border-transparent text-gray-700 dark:text-white flex-shrink-0"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
            </button>

            {isReviewMode && (
                <div className="px-4 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 rounded-full text-xs font-bold uppercase tracking-widest border border-blue-200 dark:border-blue-500/50 flex-shrink-0">
                    {language === 'fr' ? 'Mode Révision' : 'Review Mode'}
                </div>
            )}

            {/* Title — truncates if needed, never grows past its content.
                Accepts a plain string or a { fr, en } object so a switch of language
                while in-session re-renders the right label. */}
            {(() => {
                const resolved = !title ? '' :
                    typeof title === 'string' ? title :
                    (title[language] || title.en || title.fr || '');
                if (!resolved) return null;
                return (
                    <h1
                        className="text-sm md:text-base font-bold text-gray-900 dark:text-white truncate min-w-0 max-w-[40%] md:max-w-none md:flex-shrink-0"
                        title={resolved}
                    >
                        {resolved}
                    </h1>
                );
            })()}

            {/* Mini Nav — desktop only, fills remaining center space */}
            {miniNav && (
                <div data-help="session-mini-nav" className="hidden md:flex flex-1 min-w-0">
                    {miniNav}
                </div>
            )}

            <div className="flex items-center gap-3 ml-auto flex-shrink-0">
                {/* Validate Button - Top Right */}
                {!isReviewMode && !isFinished && Object.keys(answers).length > 0 && (
                    <button
                        onClick={() => setShowValidateModal(true)}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-xl font-bold hover:bg-emerald-600 transition shadow-lg shadow-emerald-500/20 text-sm flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {validateLabel || (language === 'fr' ? 'Valider l\'examen' : 'Validate Exam')}
                    </button>
                )}

                <div className="relative">
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className={`p-2 rounded-xl transition border border-gray-200 dark:border-transparent ${showSettings ? 'bg-gray-100 dark:bg-white text-gray-900 dark:text-black' : 'bg-white dark:bg-white/10 hover:bg-gray-100 dark:hover:bg-white/20 text-gray-700 dark:text-white'}`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37a1.724 1.724 0 002.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </button>

                    {showSettings && (
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl py-1.5 animate-in slide-in-from-top-2 duration-200 z-50">
                            {/* Préférences examen/quiz (synchronisées multi-appareils) */}
                            {setShowAllAnswers && (
                                <button
                                    onClick={() => setShowAllAnswers(!showAllAnswers)}
                                    className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/10 transition flex items-center justify-between gap-2 text-sm text-gray-900 dark:text-white"
                                    title={language === 'fr' ? 'Afficher toutes les réponses en relecture / flash (au lieu de seulement la vôtre et la bonne)' : 'Show all answers in review / flash (instead of only yours and the correct one)'}
                                >
                                    <span className="flex items-center gap-2.5">
                                        <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        <span>{language === 'fr' ? 'Toutes les réponses' : 'All answers'}</span>
                                    </span>
                                    <span className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${showAllAnswers ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showAllAnswers ? 'translate-x-4' : 'translate-x-0'}`} />
                                    </span>
                                </button>
                            )}
                            {setPauseSec && !isReviewMode && (
                                <div className="w-full px-4 py-2.5 flex items-center justify-between gap-2 text-sm text-gray-900 dark:text-white">
                                    <span className="flex items-center gap-2.5">
                                        <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span>{language === 'fr' ? 'Pause auto' : 'Auto-pause'}</span>
                                    </span>
                                    <button
                                        onClick={cyclePause}
                                        className="px-2 py-1 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-xs font-bold text-gray-700 dark:text-white transition min-w-[3.5rem] text-center"
                                        title={language === 'fr' ? 'Délai d\'inactivité avant mise en pause automatique' : 'Inactivity delay before auto-pause'}
                                    >
                                        {pauseLabel(pauseSec)}
                                    </button>
                                </div>
                            )}
                            <div className="mx-3 my-1 border-t border-gray-200 dark:border-white/10" />
                            {/* Language & Theme — mobile only */}
                            <div className="md:hidden">
                                <button
                                    onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
                                    className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/10 transition flex items-center gap-2.5 text-sm text-gray-900 dark:text-white"
                                >
                                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                    </svg>
                                    <span>{language === 'fr' ? 'English' : 'Français'}</span>
                                </button>
                                <button
                                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                                    className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/10 transition flex items-center gap-2.5 text-sm text-gray-900 dark:text-white"
                                >
                                    {theme === 'dark' ? (
                                        <svg className="w-4 h-4 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                        </svg>
                                    )}
                                    <span>{theme === 'dark' ? (language === 'fr' ? 'Mode clair' : 'Light mode') : (language === 'fr' ? 'Mode sombre' : 'Dark mode')}</span>
                                </button>
                                <div className="mx-3 my-1 border-t border-gray-200 dark:border-white/10" />
                            </div>
                            <button
                                onClick={() => setShowRestartModal(true)}
                                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/10 transition flex items-center gap-2.5 text-sm text-gray-900 dark:text-white"
                            >
                                <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span>{language === 'fr' ? 'Redémarrer' : 'Restart'}</span>
                            </button>
                            <button
                                onClick={async () => {
                                    await stopExam();
                                }}
                                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/10 transition flex items-center gap-2.5 text-red-500 dark:text-red-400 text-sm"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                <span>{language === 'fr' ? 'Quitter' : 'Quit'}</span>
                            </button>
                        </div>
                    )}
                </div>

                <button
                    onClick={() => setShowGrid(!showGrid)}
                    className={`p-2 rounded-xl transition border border-gray-200 dark:border-transparent flex flex-col items-center justify-center gap-1 ${showGrid ? 'bg-gray-100 dark:bg-white text-gray-900 dark:text-black' : 'bg-white dark:bg-white/10 hover:bg-gray-100 dark:hover:bg-white/20 text-gray-700 dark:text-white'}`}
                >
                    <div className="flex gap-1">
                        <div className="w-2 h-2 border border-current rounded-[3px]"></div>
                        <div className="w-2 h-2 border border-current rounded-[3px]"></div>
                    </div>
                    <div className="flex gap-1">
                        <div className="w-2 h-2 border border-current rounded-[3px]"></div>
                        <div className="w-2 h-2 border border-current rounded-[3px]"></div>
                    </div>
                </button>
                {hasAnnexe && effectiveAnnexeId && (
                    <button
                        onClick={handleAnnexeDownload}
                        disabled={loadingAnnexe}
                        className={`p-2 rounded-xl transition border border-gray-200 dark:border-transparent bg-white dark:bg-white/10 hover:bg-gray-100 dark:hover:bg-white/20 text-gray-700 dark:text-white ${loadingAnnexe ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                        title={language === 'fr' ? 'Télécharger l\'annexe PDF' : 'Download PDF appendix'}
                    >
                        {loadingAnnexe ? (
                            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        )}
                    </button>
                )}
                <div className="hidden md:flex items-center gap-2 bg-white dark:bg-white/10 rounded-xl p-1 border border-gray-200 dark:border-transparent">
                    <button
                        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/20 text-gray-700 dark:text-white transition"
                    >
                        {theme === 'dark' ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        )}
                    </button>
                    <div className="w-px h-4 bg-gray-300 dark:bg-white/20 mx-1"></div>
                    <button
                        onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
                        className="px-2.5 py-1 rounded-lg text-xs font-bold font-mono text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition"
                    >
                        {language === 'fr' ? 'EN' : 'FR'}
                    </button>
                </div>
            </div>
        </header>
    );
};

export default ExamHeader;
